// src/pages/admin/Users.jsx
import React, { useState, useEffect } from 'react';
import { useApp } from 'contexts/AppContext';
import { useAuth } from 'contexts/AuthContext';
import { authService, usersService, subscriptionService } from 'infrastructure/firebase';
import { Card, Button, Badge, Avatar, EmptyState } from 'shared/components/ui';
import { ADMIN_ROLES, isAdminRole } from 'shared/config/roles';

// Gera senha temporária no formato Aa1@xxxx (forte e legível)
function generateTempPassword() {
  const chars = 'abcdefghjkmnpqrstuvwxyz';
  const upper = 'ABCDEFGHJKMNPQRSTUVWXYZ';
  const nums  = '23456789';
  const rand  = (s) => s[Math.floor(Math.random() * s.length)];
  return rand(upper) + rand(chars) + rand(nums) + rand(nums) +
         rand(chars) + rand(chars) + rand(upper) + rand(nums);
}

const roleLabel = { clinicAdmin:'Administrador', admin:'Administrador', professional:'Profissional', client:'Cliente' };
const roleBadge = { clinicAdmin:'accent', admin:'accent', professional:'info', client:'neutral' };

const subLabel   = { ACTIVE:'Ativo', TRIAL:'Trial', EXPIRED:'Expirado', BLOCKED:'Bloqueado' };
const subVariant = { ACTIVE:'success', TRIAL:'info', EXPIRED:'danger', BLOCKED:'neutral' };

function calcDaysRemaining(accessExpiresAt) {
  const exp = accessExpiresAt?.toDate?.() ?? null;
  if (!exp) return null;
  return Math.ceil((exp - new Date()) / 86400000);
}

function fmtDate(accessExpiresAt) {
  const exp = accessExpiresAt?.toDate?.() ?? null;
  if (!exp) return '—';
  return exp.toLocaleDateString('pt-BR');
}

// ── Modal: Gerenciar assinatura ───────────────────────────────────
function SubscriptionModal({ user, onClose, onUpdate }) {
  const [busy, setBusy]           = useState(false);
  const [feedback, setFeedback]   = useState('');
  const [manualDate, setManualDate] = useState('');

  const apply = async (status, days, customDateStr) => {
    setBusy(true);
    setFeedback('');
    try {
      let expiresAt;
      if (days != null) {
        expiresAt = subscriptionService.addDaysTimestamp(days);
      } else if (customDateStr) {
        const d = new Date(customDateStr);
        d.setHours(23, 59, 59, 999);
        expiresAt = d;
      } else {
        expiresAt = null;
      }
      await onUpdate(user.id, status, expiresAt);
      setFeedback('Acesso atualizado com sucesso!');
    } catch {
      setFeedback('Erro ao atualizar acesso. Tente novamente.');
    } finally {
      setBusy(false);
    }
  };

  const days = calcDaysRemaining(user.accessExpiresAt);
  const status = user.subscriptionStatus;

  const btnStyle = (color) => ({
    padding:'8px 14px', border:`1.5px solid ${color}`, borderRadius:8, cursor:'pointer',
    background:'#fff', color, fontSize:13, fontFamily:"'DM Sans',sans-serif",
    fontWeight:500, transition:'all .15s', opacity: busy ? 0.6 : 1,
  });

  return (
    <>
      <div onClick={() => !busy && onClose()}
        style={{ position:'fixed', inset:0, background:'rgba(61,52,71,0.32)',
          backdropFilter:'blur(6px)', zIndex:200 }} />
      <div style={{
        position:'fixed', top:'50%', left:'50%', transform:'translate(-50%, -50%)',
        width:'min(500px, calc(100vw - 32px))', maxHeight:'90vh', overflowY:'auto',
        background:'#fff', borderRadius:20, boxShadow:'0 12px 40px rgba(74,63,88,0.18)',
        zIndex:201, fontFamily:"'DM Sans',sans-serif", padding:'28px 28px 24px',
      }}>
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
          <div>
            <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:20, color:'#3D3447' }}>
              Gerenciar Acesso
            </div>
            <div style={{ fontSize:13, color:'#A8A1B0', marginTop:2 }}>{user.name}</div>
          </div>
          <button onClick={onClose} disabled={busy}
            style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:'#A8A1B0', padding:4 }}>
            ✕
          </button>
        </div>

        {/* Status atual */}
        <div style={{ padding:'12px 14px', background:'#FAF7FB', borderRadius:10, marginBottom:20,
          display:'flex', gap:16, fontSize:13, flexWrap:'wrap' }}>
          <div>
            <span style={{ color:'#A8A1B0' }}>Status: </span>
            <strong style={{ color: status === 'ACTIVE' ? '#166534' : status === 'TRIAL' ? '#1e40af'
              : status === 'BLOCKED' ? '#6B6070' : '#9a3412' }}>
              {subLabel[status] ?? '—'}
            </strong>
          </div>
          <div>
            <span style={{ color:'#A8A1B0' }}>Expira em: </span>
            <strong style={{ color:'#3D3447' }}>{fmtDate(user.accessExpiresAt)}</strong>
          </div>
          {days != null && (
            <div>
              <span style={{ color:'#A8A1B0' }}>Restam: </span>
              <strong style={{ color: days < 0 ? '#9a3412' : days <= 7 ? '#854D0E' : '#166534' }}>
                {days < 0 ? 'Vencido' : `${days} dia${days !== 1 ? 's' : ''}`}
              </strong>
            </div>
          )}
        </div>

        {feedback && (
          <div style={{ padding:'10px 14px', background: feedback.startsWith('Erro') ? '#F0DADD' : '#f0fdf4',
            border:`1px solid ${feedback.startsWith('Erro') ? '#C9929A' : '#86efac'}`,
            borderRadius:8, color: feedback.startsWith('Erro') ? '#7A3C44' : '#166534',
            fontSize:13, marginBottom:16 }}>
            {feedback}
          </div>
        )}

        {/* Trial */}
        <div style={{ marginBottom:18 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'#6B6070', textTransform:'uppercase',
            letterSpacing:.8, marginBottom:8 }}>Liberar Trial</div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {[7, 15, 30].map(d => (
              <button key={d} disabled={busy} onClick={() => apply('TRIAL', d)}
                style={btnStyle('#1e40af')}>
                {d} dias
              </button>
            ))}
          </div>
        </div>

        {/* Assinatura */}
        <div style={{ marginBottom:18 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'#6B6070', textTransform:'uppercase',
            letterSpacing:.8, marginBottom:8 }}>Liberar Assinatura</div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {[30, 90, 180, 365].map(d => (
              <button key={d} disabled={busy} onClick={() => apply('ACTIVE', d)}
                style={btnStyle('#166534')}>
                {d === 365 ? '1 ano' : `${d} dias`}
              </button>
            ))}
          </div>
        </div>

        {/* Data manual */}
        <div style={{ marginBottom:18 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'#6B6070', textTransform:'uppercase',
            letterSpacing:.8, marginBottom:8 }}>Definir data manual</div>
          <div style={{ display:'flex', gap:8 }}>
            <input type="date" value={manualDate} onChange={e => setManualDate(e.target.value)}
              min={new Date().toISOString().slice(0,10)}
              style={{ flex:1, padding:'9px 12px', border:'1.5px solid #E8E5EC', borderRadius:8,
                fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:'none', color:'#3D3447' }} />
            <button disabled={busy || !manualDate} onClick={() => apply('ACTIVE', null, manualDate)}
              style={{ ...btnStyle('#166534'), opacity: busy || !manualDate ? 0.5 : 1 }}>
              Aplicar
            </button>
          </div>
        </div>

        <hr style={{ border:'none', borderTop:'1px solid #E8E5EC', margin:'4px 0 16px' }} />

        {/* Ações de status */}
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <button disabled={busy || status === 'ACTIVE'} onClick={() => apply('ACTIVE', null, null)}
            style={{ ...btnStyle('#166534'), opacity: busy || status === 'ACTIVE' ? 0.5 : 1 }}>
            ✓ Ativar sem expiração
          </button>
          <button disabled={busy || status === 'BLOCKED'} onClick={() => apply('BLOCKED', null, null)}
            style={{ ...btnStyle('#9a3412'), opacity: busy || status === 'BLOCKED' ? 0.5 : 1 }}>
            🚫 Bloquear acesso
          </button>
        </div>

        <div style={{ marginTop:20, textAlign:'right' }}>
          <button onClick={onClose} disabled={busy}
            style={{ padding:'9px 20px', background:'#F3F0F7', border:'none', borderRadius:10,
              fontSize:13, color:'#6B6070', cursor:'pointer', fontFamily:"'DM Sans',sans-serif" }}>
            Fechar
          </button>
        </div>
      </div>
    </>
  );
}

// ── Tab 1: Cadastrar usuário ────────────────────────────────────
function UserSignupForm({ showToast, estabelecimentoId }) {
  const [form, setForm] = useState({
    name:'', email:'', role:'professional', phone:'', specialty:'',
    sendInvite:true, tempPassword:true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name || !form.email) {
      showToast('Preencha nome e e-mail.', 'warning');
      return;
    }
    setSaving(true);
    try {
      // Gera senha temporária segura
      const tempPass = form.tempPassword ? generateTempPassword() : 'agenda123';
      // Passo 1: cria o usuário SEM vínculo de estabelecimento. O register() roda
      // como o próprio usuário novo (app secundário), então o doc nasce não-afiliado
      // — as regras só permitem auto-cadastro de staff SEM estab (anti-escalonamento).
      const created = await authService.register({
        email:             form.email,
        password:          tempPass,
        name:              form.name,
        role:              form.role,
        phone:             form.phone || '',
        specialty:         form.specialty || '',
        estabelecimentoId: null,
      });
      // Passo 2: o admin (sessão atual) vincula o novo usuário ao SEU estabelecimento.
      // As regras permitem este "claim" só quando o doc ainda não tem estab.
      if (estabelecimentoId && created?.uid) {
        await usersService.update(created.uid, { estabelecimentoId });
      }
      showToast(`Convite enviado para ${form.email} com senha provisória${form.tempPassword ? ': ' + tempPass : ''}.`);
      setForm({ name:'', email:'', role:'professional', phone:'', specialty:'', sendInvite:true, tempPassword:true });
    } catch (e) {
      const msg = e.code === 'auth/email-already-in-use'
        ? 'Este e-mail já está cadastrado no sistema.'
        : e.code === 'auth/invalid-email'
        ? 'E-mail inválido.'
        : e.code === 'auth/weak-password'
        ? 'Senha muito fraca (mínimo 6 caracteres).'
        : 'Erro ao cadastrar usuário. Tente novamente.';
      showToast(msg, 'danger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth:640 }}>
      <div style={{ background:'#fff', border:'1px solid #E8E5EC', borderRadius:20, padding:'28px 32px' }}>
        <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:22, color:'#3D3447', marginBottom:4 }}>
          Cadastrar novo usuário
        </div>
        <p style={{ fontSize:13, color:'#A8A1B0', fontFamily:"'DM Sans',sans-serif", lineHeight:1.5, marginBottom:24 }}>
          O usuário receberá um e-mail com a senha provisória. Ele será solicitado a alterá-la no primeiro login.
        </p>

        <div style={{ marginBottom:18 }}>
          <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>
            Nome completo <span style={{ color:'#C9929A' }}>*</span>
          </label>
          <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ex.: Mariana Silva"
            style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #E8E5EC', borderRadius:12, fontSize:14, fontFamily:"'DM Sans',sans-serif", outline:'none', boxSizing:'border-box', color:'#3D3447', background:'#fff' }} />
        </div>

        <div style={{ marginBottom:18 }}>
          <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>
            E-mail <span style={{ color:'#C9929A' }}>*</span>
          </label>
          <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="usuario@agendapro.com"
            style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #E8E5EC', borderRadius:12, fontSize:14, fontFamily:"'DM Sans',sans-serif", outline:'none', boxSizing:'border-box', color:'#3D3447', background:'#fff' }} />
          <div style={{ fontSize:11, color:'#A8A1B0', marginTop:4 }}>Será usado para login e envio de senha provisória.</div>
        </div>

        <div style={{ marginBottom:18 }}>
          <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:8 }}>
            Perfil de acesso <span style={{ color:'#C9929A' }}>*</span>
          </label>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            {[
              { id:'clinicAdmin', title:'Administrador', desc:'Gestão da clínica — agendamentos, finanças, cadastros e usuários do estabelecimento.' },
              { id:'professional', title:'Profissional',  desc:'Vê sua agenda, marca atendimentos e cadastra procedimentos.' },
            ].map(opt => (
              <div key={opt.id} onClick={() => set('role', opt.id)}
                style={{
                  border:`1.5px solid ${form.role === opt.id ? '#B5A2D2' : '#E8E5EC'}`,
                  background: form.role === opt.id ? '#E6DEF0' : '#fff',
                  borderRadius:14, padding:'14px 16px', cursor:'pointer', fontFamily:"'DM Sans',sans-serif",
                  transition:'all .15s',
                }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <div style={{
                    width:16, height:16, borderRadius:'50%',
                    border:`2px solid ${form.role === opt.id ? '#B5A2D2' : '#E8E5EC'}`,
                    background: form.role === opt.id ? '#B5A2D2' : '#fff',
                    display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
                  }}>
                    {form.role === opt.id && <span style={{ width:6, height:6, borderRadius:'50%', background:'#fff' }} />}
                  </div>
                  <div style={{ fontWeight:600, fontSize:13, color:'#3D3447' }}>{opt.title}</div>
                </div>
                <div style={{ fontSize:11, color:'#6B6070', lineHeight:1.5, paddingLeft:24 }}>{opt.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {form.role === 'professional' && (
          <div style={{ marginBottom:18 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>
              Especialidade
            </label>
            <input value={form.specialty} onChange={e => set('specialty', e.target.value)} placeholder="Ex: Dermatologia, Estética..."
              style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #E8E5EC', borderRadius:12, fontSize:14, fontFamily:"'DM Sans',sans-serif", outline:'none', boxSizing:'border-box', color:'#3D3447', background:'#fff' }} />
          </div>
        )}

        <div style={{ marginBottom:18 }}>
          <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>
            Telefone / WhatsApp
          </label>
          <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="(11) 99999-9999"
            style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #E8E5EC', borderRadius:12, fontSize:14, fontFamily:"'DM Sans',sans-serif", outline:'none', boxSizing:'border-box', color:'#3D3447', background:'#fff' }} />
        </div>

        <hr style={{ border:'none', borderTop:'1px solid #E8E5EC', margin:'8px 0 18px' }} />

        <div style={{ marginBottom:12 }}>
          <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', fontSize:13, color:'#6B6070' }}>
            <input type="checkbox" checked={form.sendInvite} onChange={e => set('sendInvite', e.target.checked)}
              style={{ accentColor: '#B5A2D2', width:16, height:16 }} />
            Enviar e-mail de boas-vindas com instruções
          </label>
        </div>

        <div style={{ marginBottom:18 }}>
          <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', fontSize:13, color:'#6B6070' }}>
            <input type="checkbox" checked={form.tempPassword} onChange={e => set('tempPassword', e.target.checked)}
              style={{ accentColor: '#B5A2D2', width:16, height:16 }} />
            Gerar senha provisória automaticamente
          </label>
        </div>

        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:24 }}>
          <Button variant="secondary" onClick={() => setForm({ name:'', email:'', role:'professional', phone:'', specialty:'', sendInvite:true, tempPassword:true })}>
            Limpar
          </Button>
          <Button variant="primary" disabled={!form.name || !form.email || saving} loading={saving} onClick={handleSubmit}>
            Cadastrar usuário
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Modal: Reset de senha ─────────────────────────────────────────
function ResetPasswordModal({ user, onConfirm, onCancel, loading, done }) {
  return (
    <>
      <div onClick={!loading ? onCancel : undefined} style={{ position:'fixed', inset:0, background:'rgba(61,52,71,0.32)',
        backdropFilter:'blur(6px)', zIndex:150 }} />
      <div style={{
        position:'fixed', top:'50%', left:'50%', transform:'translate(-50%, -50%)',
        width:'min(460px, calc(100vw - 32px))', background:'#fff', borderRadius:20,
        boxShadow:'0 12px 40px rgba(74,63,88,0.18)', zIndex:151, overflow:'hidden',
        fontFamily:"'DM Sans',sans-serif",
      }}>
        <div style={{ padding:'28px 28px 8px', textAlign:'center' }}>
          <div style={{ width:56, height:56, borderRadius:'50%', background:'#E6DEF0', color:'#5A4E66',
            margin:'0 auto 14px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24 }}>🔑</div>
          <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:22, color:'#3D3447', marginBottom:6 }}>
            {done ? 'E-mail enviado!' : `Redefinir senha de ${user.name?.split(' ')[0] ?? 'usuário'}?`}
          </div>
          {!done ? (
            <p style={{ fontSize:13, color:'#6B6070', lineHeight:1.55, margin:'0 0 16px' }}>
              Um link seguro de redefinição será enviado para<br/>
              <span style={{ color:'#6B5876', fontWeight:600 }}>{user.email}</span>
            </p>
          ) : (
            <div style={{ fontSize:13, color:'#166534', background:'#f0fdf4', border:'1px solid #86efac',
              borderRadius:10, padding:'12px 16px', margin:'0 0 8px' }}>
              Link de redefinição enviado para <strong>{user.email}</strong>. Peça ao usuário para verificar a caixa de entrada e o spam.
            </div>
          )}
        </div>
        <div style={{ padding:'16px 28px 24px', display:'flex', justifyContent:'center', gap:10 }}>
          {!done
            ? <>
                <Button variant="secondary" onClick={onCancel} disabled={loading}>Cancelar</Button>
                <Button variant="primary" onClick={onConfirm} loading={loading} disabled={loading}>
                  {loading ? 'Enviando...' : '📧 Enviar link de redefinição'}
                </Button>
              </>
            : <Button variant="primary" onClick={onCancel}>Fechar</Button>
          }
        </div>
      </div>
    </>
  );
}

// ── Tab 2: Usuários cadastrados ─────────────────────────────────
function UsersList({ showToast, estabelecimentoId }) {
  const { isSuperAdmin, updateSubscription } = useAuth();
  const [users, setUsers]         = useState([]);
  const [query, setQuery]         = useState('');
  const [confirm, setConfirm]     = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [subModal, setSubModal]   = useState(null); // user cujo acesso está sendo gerenciado
  // edição inline de nome
  const [editing, setEditing]     = useState(null); // { id, name }
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    const unsub = usersService.onSnapshot((all) => {
      // Super admin (sem estabId) vê todos; clinic admin filtra pelo próprio estabelecimento
      const staffRoles = new Set([...ADMIN_ROLES, 'professional']);
      const visible = estabelecimentoId
        ? all.filter(u => staffRoles.has(u.role) && u.estabelecimentoId === estabelecimentoId)
        : all.filter(u => staffRoles.has(u.role));
      setUsers(visible);
    });
    return () => unsub && unsub();
  }, [estabelecimentoId]);

  const startEdit = (u) => setEditing({ id: u.id, name: u.name });

  const handleEditSave = async () => {
    if (!editing?.name?.trim()) {
      showToast('Nome não pode ser vazio.', 'warning');
      return;
    }
    setSavingEdit(true);
    try {
      await usersService.update(editing.id, { name: editing.name.trim() });
      showToast('Nome atualizado com sucesso!');
      setEditing(null);
    } catch {
      showToast('Erro ao atualizar nome.', 'danger');
    } finally {
      setSavingEdit(false);
    }
  };

  const filtered = users.filter(u => {
    if (!query) return true;
    const q = query.toLowerCase();
    return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  const handleResetPassword = async () => {
    if (!confirm) return;
    setResetting(true);
    try {
      await authService.resetPassword(confirm.user.email);
      setResetDone(true);
      showToast(`Link de redefinição enviado para ${confirm.user.email}`);
    } catch (err) {
      console.error('[Reset senha]', err);
      const msg = err?.code === 'auth/user-not-found'
        ? 'Usuário não encontrado no Firebase Auth.'
        : 'Erro ao enviar link de redefinição.';
      showToast(msg, 'danger');
    } finally {
      setResetting(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await usersService.update(user.id, { active: !user.active });
      showToast(user.active ? `${user.name.split(' ')[0]} desativado` : `${user.name.split(' ')[0]} reativado`);
    } catch {
      showToast('Erro ao atualizar usuário.', 'danger');
    }
  };

  const handleUpdateSubscription = async (uid, status, expiresAt) => {
    await updateSubscription(uid, status, expiresAt);
    // Atualiza a lista local imediatamente sem aguardar re-snapshot
    setUsers(prev => prev.map(u => u.id === uid
      ? { ...u, subscriptionStatus: status, accessExpiresAt: expiresAt }
      : u
    ));
    showToast('Acesso atualizado com sucesso!');
  };

  const headers = isSuperAdmin
    ? ['Usuário','Perfil','Assinatura','Expira em','Dias','Último acesso','Status','Ações']
    : ['Usuário','Perfil','Último acesso','Status','Cadastrado em','Ações'];

  return (
    <>
      <div style={{ display:'flex', gap:10, marginBottom:18, alignItems:'center', flexWrap:'wrap', fontFamily:"'DM Sans',sans-serif" }}>
        <div style={{ position:'relative', flex:1, minWidth:240, maxWidth:380 }}>
          <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#A8A1B0', fontSize:14 }}>🔍</span>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Buscar usuário..."
            style={{ width:'100%', padding:'9px 14px 9px 36px', border:'1.5px solid #E8E5EC',
              borderRadius:12, fontSize:14, fontFamily:"'DM Sans',sans-serif", outline:'none', boxSizing:'border-box',
              color:'#3D3447', background:'#fff' }} />
        </div>
      </div>

      <Card>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13, fontFamily:"'DM Sans',sans-serif" }}>
            <thead>
              <tr>
                {headers.map((h,i) => (
                  <th key={i} style={{ textAlign:'left', padding:'10px 16px', fontSize:11, fontWeight:600,
                    textTransform:'uppercase', letterSpacing:1, color:'#A8A1B0',
                    borderBottom:'1px solid #E8E5EC', background:'#FAF7FB', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={headers.length}><EmptyState icon="👥" title="Nenhum usuário encontrado" /></td></tr>
              ) : filtered.map(u => (
                <tr key={u.id}>
                  <td style={{ padding:'14px 16px', borderBottom:'1px solid #E8E5EC' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <Avatar name={u.name} color={u.avatarColor || (isAdminRole(u.role) ? 'lavender' : 'mint')} size="md" />
                      <div style={{ flex:1, minWidth:0 }}>
                        {editing?.id === u.id ? (
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <input
                              autoFocus
                              value={editing.name}
                              onChange={e => setEditing(ed => ({ ...ed, name: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') handleEditSave(); if (e.key === 'Escape') setEditing(null); }}
                              style={{ padding:'5px 9px', border:'1.5px solid #B5A2D2', borderRadius:8,
                                fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:'none',
                                color:'#3D3447', width:160 }}
                            />
                            <Button variant="primary" size="sm" loading={savingEdit} onClick={handleEditSave}>✓</Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>✕</Button>
                          </div>
                        ) : (
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <div style={{ fontWeight:500, color: u.active ? '#3D3447' : '#A8A1B0', textDecoration: u.active ? 'none' : 'line-through' }}>
                              {u.name}
                            </div>
                            <button onClick={() => startEdit(u)} title="Editar nome"
                              style={{ background:'none', border:'none', cursor:'pointer', color:'#B5A2D2', fontSize:13, padding:'2px 4px', borderRadius:6, lineHeight:1 }}>
                              ✏️
                            </button>
                          </div>
                        )}
                        <div style={{ fontSize:11, color:'#A8A1B0', marginTop:2 }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding:'14px 16px', borderBottom:'1px solid #E8E5EC' }}>
                    <Badge variant={roleBadge[u.role]}>{roleLabel[u.role]}</Badge>
                  </td>
                  {isSuperAdmin && (() => {
                    const days = calcDaysRemaining(u.accessExpiresAt);
                    const subStatus = u.subscriptionStatus;
                    return (
                      <>
                        <td style={{ padding:'14px 16px', borderBottom:'1px solid #E8E5EC' }}>
                          {subStatus
                            ? <Badge variant={subVariant[subStatus] ?? 'neutral'}>{subLabel[subStatus]}</Badge>
                            : <span style={{ color:'#A8A1B0', fontSize:12 }}>—</span>}
                        </td>
                        <td style={{ padding:'14px 16px', borderBottom:'1px solid #E8E5EC', color:'#6B6070', fontSize:12, whiteSpace:'nowrap' }}>
                          {fmtDate(u.accessExpiresAt)}
                        </td>
                        <td style={{ padding:'14px 16px', borderBottom:'1px solid #E8E5EC', fontSize:12, whiteSpace:'nowrap' }}>
                          {days == null
                            ? <span style={{ color:'#A8A1B0' }}>—</span>
                            : days < 0
                              ? <span style={{ color:'#9a3412', fontWeight:600 }}>Vencido</span>
                              : <span style={{ color: days <= 7 ? '#854D0E' : '#166534', fontWeight:600 }}>{days}d</span>}
                        </td>
                      </>
                    );
                  })()}
                  <td style={{ padding:'14px 16px', borderBottom:'1px solid #E8E5EC', color:'#6B6070' }}>{u.lastLogin || '—'}</td>
                  <td style={{ padding:'14px 16px', borderBottom:'1px solid #E8E5EC' }}>
                    {u.active
                      ? <Badge variant="success">✓ Ativo</Badge>
                      : <Badge variant="neutral">○ Inativo</Badge>}
                  </td>
                  {!isSuperAdmin && (
                    <td style={{ padding:'14px 16px', borderBottom:'1px solid #E8E5EC', color:'#A8A1B0', fontSize:12 }}>
                      {u.joined ? new Date(u.joined).toLocaleDateString('pt-BR') : '—'}
                    </td>
                  )}
                  <td style={{ padding:'14px 16px', borderBottom:'1px solid #E8E5EC', whiteSpace:'nowrap' }}>
                    {isSuperAdmin && (
                      <Button variant="secondary" size="sm"
                        onClick={() => setSubModal(u)}
                        style={{ marginRight:6 }}>
                        🔑 Assinatura
                      </Button>
                    )}
                    <Button variant="secondary" size="sm"
                      onClick={() => { setResetDone(false); setConfirm({ user: u }); }}
                      style={{ marginRight:6 }}>
                      🔑 Senha
                    </Button>
                    <Button variant="ghost" size="sm" title={u.active ? 'Desativar' : 'Reativar'}
                      onClick={() => handleToggleActive(u)}>
                      {u.active ? '🚫' : '✓'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {confirm && (
        <ResetPasswordModal
          user={confirm.user}
          onCancel={() => { setConfirm(null); setResetDone(false); }}
          onConfirm={handleResetPassword}
          loading={resetting}
          done={resetDone} />
      )}

      {subModal && (
        <SubscriptionModal
          user={subModal}
          onClose={() => setSubModal(null)}
          onUpdate={handleUpdateSubscription} />
      )}
    </>
  );
}

// ── Main view with sub-tabs ─────────────────────────────────────
export default function AdminUsers() {
  const { showToast } = useApp();
  const { userProfile } = useAuth();
  const [tab, setTab] = useState('list');
  const estabelecimentoId = userProfile?.estabelecimentoId || null;

  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:32, color:'#3D3447' }}>Usuários</div>
        <div style={{ fontSize:13, color:'#A8A1B0', marginTop:2 }}>
          Logins de funcionários e profissionais — não confundir com a tela de Clientes.
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{ display:'flex', gap:0, marginBottom:24, borderBottom:'1px solid #E8E5EC' }}>
        {[
          { id:'list',   label:'Usuários cadastrados' },
          { id:'signup', label:'Cadastrar usuário' },
        ].map(t => (
          <div key={t.id} onClick={() => setTab(t.id)}
            style={{
              padding:'10px 18px', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", fontSize:13,
              fontWeight: tab === t.id ? 600 : 500,
              color: tab === t.id ? '#3D3447' : '#A8A1B0',
              borderBottom: tab === t.id ? '2px solid #B5A2D2' : '2px solid transparent',
              marginBottom:-1, transition:'all .15s',
            }}>
            {t.label}
          </div>
        ))}
      </div>

      {tab === 'list'   && <UsersList   showToast={showToast} estabelecimentoId={estabelecimentoId} />}
      {tab === 'signup' && <UserSignupForm showToast={showToast} estabelecimentoId={estabelecimentoId} />}
    </div>
  );
}
